import {useCallback, useState} from 'react';
import {useAppDispatch, useAppSelector} from '@/store';
import {setToastNotifications} from '@/store/slices/commonSlice';
import {
  resetPerfectStore,
  setPerfectStoreFormData
} from '@/store/slices/perfectStoreSlice';
import {
  addDraftPerfectStore,
  publishPerfectStore,
  updateDraftPerfectStore,
  updatePerfectStore,
  validatePerfectStoreName,
  importFirstPositionTargetFile
} from '@/services/perfectStore';
import {getUUID, handleNameValidationViaCallbackFn} from '@/utils/commons';
import {buildPerfectStorePayload} from '@/pages/PerfectStoreUtils';
import {Constants} from '@/utils/constants/commonConstants';
import {logger} from '@/helpers/logger';

export const usePerfectStore = () => {
  const dispatch = useAppDispatch();
  const perfectStore = useAppSelector((state) => state?.perfectStore);
  const [isNameValid, setIsNameValid] = useState(true);

  const handleNameValidation = useCallback(async (queryParams) => {
    return handleNameValidationViaCallbackFn(
      validatePerfectStoreName,
      queryParams
    );
  }, []);

  const handleValidationModalClose = useCallback(() => {
    setIsNameValid(true);
  }, []);

  const handleDraft = useCallback(
    async (formData) => {
      const hasExistingUuid = !!perfectStore?.uuid;
      const uuid = hasExistingUuid
        ? perfectStore.uuid
        : getUUID(formData?.name || 'perfect-store');

      try {
        dispatch(setPerfectStoreFormData(formData));
        const payload = buildPerfectStorePayload(formData, {
          status: Constants.Draft,
          uuid
        });

        const response = hasExistingUuid
          ? await updateDraftPerfectStore({data: payload})
          : await addDraftPerfectStore({data: payload});

        if (response?.success) {
          dispatch(
            setToastNotifications({
              show: true,
              message: hasExistingUuid
                ? 'Perfect Store draft updated'
                : 'New Perfect Store draft',
              type: 'success'
            })
          );
          dispatch(resetPerfectStore());
          return {success: true, payload, response};
        }

        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to save draft',
            type: 'error'
          })
        );
        return {success: false, response};
      } catch (error) {
        logger.error('Error saving Perfect Store draft:', error);
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Failed to save draft',
            type: 'error'
          })
        );
        return {success: false};
      }
    },
    [dispatch, perfectStore?.uuid]
  );

  const handlePublish = useCallback(
    async (formData) => {
      const isEditPublished = !!perfectStore?.id;
      const hasExistingUuid = !!perfectStore?.uuid;
      const uuid = hasExistingUuid
        ? perfectStore.uuid
        : getUUID(formData?.name || 'perfect-store');

      try {
        dispatch(setPerfectStoreFormData(formData));
        const payload = buildPerfectStorePayload(formData, {
          status: Constants.Published,
          uuid
        });

        const response = isEditPublished
          ? await updatePerfectStore({data: payload})
          : await publishPerfectStore({data: payload});

        if (response?.success) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Perfect Store Target published successfully.',
              type: 'success'
            })
          );
          dispatch(resetPerfectStore());
          return {success: true, payload, response};
        }

        dispatch(
          setToastNotifications({
            show: true,
            message: 'Unable to publish the target. Please try again.',
            type: 'error'
          })
        );
        return {success: false, response};
      } catch (error) {
        logger.error('Error publishing Perfect Store:', error);
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Unable to publish the target. Please try again.',
            type: 'error'
          })
        );
        return {success: false};
      }
    },
    [dispatch, perfectStore?.id, perfectStore?.uuid]
  );

  const onTemplateUpdate = useCallback(
    async (id, requestData) => {
      try {
        const response = await importFirstPositionTargetFile(id, {
          fileUrl: requestData
        });
        if (response?.success) {
          dispatch(
            setToastNotifications({
              show: true,
              message: 'Template Updated Successfully',
              type: 'success'
            })
          );
          return true;
        }
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Template Update Failed',
            type: 'error'
          })
        );
        return false;
      } catch (error) {
        logger.error('Error in onTemplateUpdate:', error);
        dispatch(
          setToastNotifications({
            show: true,
            message: 'Template Update Failed',
            type: 'error'
          })
        );
        return false;
      }
    },
    [dispatch]
  );

  return {
    handleDraft,
    handlePublish,
    handleNameValidation,
    isNameValid,
    setIsNameValid,
    handleValidationModalClose,
    onTemplateUpdate
  };
};
